// INVITE LIFECYCLE (plan §5.1.6, decision #9): pure, DB-free rules for
// invite expiry, reminder throttling, and round auto-close. Consumed by
// the daily reminders tick (src/lib/reminders.ts) and the round-open API
// so the boundary conditions live in one tested place.

export type InviteStatusValue =
  | "PENDING"
  | "SENT"
  | "OPENED"
  | "IN_PROGRESS"
  | "COMPLETED"
  | "EXPIRED";

// Statuses a live invite can hold — anything here past expires_at flips to
// EXPIRED on the daily tick. COMPLETED never expires (the work is in);
// EXPIRED is terminal.
export const EXPIRABLE_STATUSES: InviteStatusValue[] = ["PENDING", "SENT", "OPENED", "IN_PROGRESS"];

export const MAX_REMINDERS = 2; // day-1 + day-2 nudges inside the 3-day window

// "At most one reminder per invite per day" — but the scheduler is a daily
// tick with boot-time jitter, so a strict 24h anchor would skip every other
// day when today's tick lands a few minutes earlier than yesterday's. 20h
// keeps the one-per-day guarantee while tolerating tick drift.
export const MIN_REMINDER_GAP_MS = 20 * 60 * 60 * 1000;

export function computeExpiresAt(from: Date, expiryDays: number): Date {
  return new Date(from.getTime() + expiryDays * 24 * 60 * 60 * 1000);
}

export function isInviteExpired(invite: { status: string; expires_at: Date }, now: Date): boolean {
  return (
    EXPIRABLE_STATUSES.includes(invite.status as InviteStatusValue) &&
    invite.expires_at.getTime() <= now.getTime()
  );
}

export type ReminderInvite = {
  status: string;
  expires_at: Date;
  sent_at: Date | null;
  last_reminder_at: Date | null;
  reminder_count: number;
};

// A reminder goes out only when ALL hold:
//  - the invite email actually went out (SENT/OPENED/IN_PROGRESS — a PENDING
//    invite never got its first email; re-send, don't remind)
//  - the link hasn't expired
//  - fewer than MAX_REMINDERS reminders sent
//  - at least MIN_REMINDER_GAP_MS since the last reminder, or since the
//    original send when no reminder has gone out yet
export function isReminderEligible(invite: ReminderInvite, now: Date): boolean {
  if (invite.status !== "SENT" && invite.status !== "OPENED" && invite.status !== "IN_PROGRESS") {
    return false;
  }
  if (invite.expires_at.getTime() <= now.getTime()) return false;
  if (invite.reminder_count >= MAX_REMINDERS) return false;

  const anchor = invite.last_reminder_at ?? invite.sent_at;
  if (!anchor) return false; // no send timestamp — nothing to remind about
  return now.getTime() - anchor.getTime() >= MIN_REMINDER_GAP_MS;
}

// A round closes when every invite reached a terminal state. Zero invites
// never auto-closes (an OPEN round always has invites; belt-and-braces).
export function shouldCloseRound(inviteStatuses: string[]): boolean {
  if (inviteStatuses.length === 0) return false;
  return inviteStatuses.every((s) => s === "COMPLETED" || s === "EXPIRED");
}
